import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import Container from '@mui/material/Container';
import CssBaseline from '@mui/material/CssBaseline';
import Avatar from '@mui/material/Avatar';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Grid from '@mui/material/Grid';
import Link from '@mui/material/Link';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { makeStyles } from '@mui/styles';
import QRCode from 'react-qr-code';
import T from 'i18n';
import { OtpForm } from 'features/common';
import { useTotpEnrollment } from './redux/totp';
import api from 'common/api';

const useStyles = makeStyles({
  paper: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    fontSize: '1rem',
  },
  avatar: {
    backgroundColor: '#f50057 !important',
  },
  form: {
    width: '100%',
    marginTop: '2rem',
  },
  section: {
    width: '100%',
    marginTop: '1.5rem',
  },
  manualKeyBox: {
    padding: '1rem',
    backgroundColor: '#F3F3F3',
    borderRadius: '0.25rem',
    wordBreak: 'break-all',
  },
});

function useQuery() {
  const { search } = useLocation();
  return useMemo(() => new URLSearchParams(search), [search]);
}

const pollUntil = async (fn: () => Promise<boolean>, attempts = 10, delay = 800) => {
  for (let i = 0; i < attempts; i++) {
    const ok = await fn();
    if (ok) return true;
    await new Promise((r) => setTimeout(r, delay));
  }
  return false;
};

export default function SetupAuthenticator({ onFinished }: { onFinished?: () => void }) {
  const classes = useStyles();
  const dispatch = useDispatch();
  const history = useHistory();
  const qs = useQuery();

  const { startTotpEnrollment, verifyTotpEnrollment, clearTotpEnrollment } = useTotpEnrollment();
  const totpSecret = useSelector((s: any) => s.auth.totpSecret);
  const totpUri = useSelector((s: any) => s.auth.totpUri);
  const totpSetupLoading = useSelector((s: any) => s.auth.totpSetupLoading);
  const totpSetupError = useSelector((s: any) => s.auth.totpSetupError);
  const mustUseAuthenticatorApp = useSelector((s: any) => s.auth.mustUseAuthenticatorApp);
  const hasAuthenticatorApp = useSelector((s: any) => s.auth.hasAuthenticatorApp);

  const [otpValue, setOtpValue] = useState('');
  const [disableSubmit, setDisableSubmit] = useState(true);
  const [finishing, setFinishing] = useState(false);
  const [finishError, setFinishError] = useState<string | null>(null);
  const [showManual, setShowManual] = useState(false);

  const next = qs.get('next') ? decodeURIComponent(qs.get('next')!) : '/';

  useEffect(() => {
    startTotpEnrollment();
  }, []);

  const regenerate = useCallback(async () => {
    clearTotpEnrollment();
    await startTotpEnrollment();
  }, [clearTotpEnrollment, startTotpEnrollment]);

  const finishSetup = useCallback(async () => {
    setFinishing(true);
    setFinishError(null);
    const ok = await pollUntil(
      async () => {
        const res: any = await api.get(`/users-me?ts=${Date.now()}`);
        if (res && res.error) return false;
        // Update both auth and home slices by reusing existing success action
        dispatch({ type: 'HOME_FETCH_CASES_SUCCESS', data: res });
        return !!(res.hasAuthenticatorApp || res.mustUseAuthenticatorApp === false);
      },
      10,
      800,
    );

    setFinishing(false);
    if (!ok) {
      setFinishError(T.translate('auth.setup.finishWaitError'));
      return;
    }
    if (onFinished) onFinished();
    else history.replace(next || '/');
  }, [dispatch, history, next]);

  const onVerify = useCallback(async () => {
    try {
      await verifyTotpEnrollment(otpValue);
      await finishSetup();
    } catch {
      // Errors are surfaced via state/totpSetupError
    }
  }, [finishSetup, otpValue, verifyTotpEnrollment]);

  const doneAlready = hasAuthenticatorApp || mustUseAuthenticatorApp === false;
  const goBackToSignIn = useCallback(() => {
    clearTotpEnrollment();
    dispatch({ type: 'AUTH_LOGOUT' });
    history.replace('/signin');
  }, [clearTotpEnrollment, dispatch, history]);

  useEffect(() => {
    if (doneAlready) {
      if (onFinished) onFinished();
      else history.replace(next || '/');
    }
  }, [doneAlready, history, next, onFinished]);

  return (
    <Container component="main" maxWidth="sm">
      <CssBaseline />
      <div className={classes.paper}>
        <Avatar className={classes.avatar} sx={{ m: 1 }}>
          <LockOutlinedIcon />
        </Avatar>
        <Typography component="h1" variant="h5">
          {T.translate('auth.setup.title')}
        </Typography>
        <Typography sx={{ mt: 1, textAlign: 'center' }}>
          {T.translate('auth.setup.description')}
        </Typography>

        <Grid container spacing={2} className={classes.section} alignItems="center">
          <Grid item xs={12} md={6} sx={{ textAlign: 'center' }}>
            {totpUri ? (
              <>
                <Typography sx={{ mb: 1 }}>{T.translate('auth.setup.scanQrLabel')}</Typography>
                <div style={{ background: 'white', padding: 12, display: 'inline-block' }}>
                  <QRCode value={totpUri} size={164} />
                </div>
              </>
            ) : (
              <Typography>{T.translate('auth.setup.generatingQr')}</Typography>
            )}
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography sx={{ fontWeight: 600 }}>
              {T.translate('auth.setup.manualKeyLabel')}
            </Typography>
            <div className={classes.manualKeyBox}>
              {totpSecret || T.translate('auth.setup.generatingKey')}
            </div>
            <Typography variant="body2" sx={{ mt: 1 }}>
              {T.translate('auth.setup.manualKeyHelp')}{' '}
              <Link component="button" onClick={() => setShowManual((s) => !s)}>
                {showManual
                  ? T.translate('auth.setup.hideManualHelp')
                  : T.translate('auth.setup.showManualHelp')}
              </Link>
            </Typography>
            {showManual && (
              <Typography variant="body2" sx={{ mt: 1 }}>
                {T.translate('auth.setup.verificationInfo')}
              </Typography>
            )}
          </Grid>
        </Grid>

        <div className={classes.section}>
          <OtpForm
            defaultValue={otpValue}
            label={T.translate('auth.setup.verificationCodeLabel')}
            setOtpValue={setOtpValue}
            setDisableSubmit={setDisableSubmit}
          />
          {totpSetupError && (
            <Typography color="error" sx={{ mt: 1 }}>
              {totpSetupError}
            </Typography>
          )}
        </div>

        {finishing && <Typography sx={{ mt: 1 }}>{T.translate('auth.setup.finishing')}</Typography>}
        {finishError && (
          <Typography color="error" sx={{ mt: 1 }}>
            {finishError}{' '}
            <Link component="button" onClick={finishSetup}>
              {T.translate('auth.setup.preparing')}
            </Link>
          </Typography>
        )}

        <Grid container spacing={2} sx={{ mt: 2 }}>
          <Grid item xs={12} md={6}>
            <Button
              fullWidth
              variant="contained"
              color="primary"
              onClick={onVerify}
              disabled={totpSetupLoading || finishing || disableSubmit}
            >
              {totpSetupLoading ? (
                <CircularProgress size={17} />
              ) : (
                T.translate('auth.setup.verifyAndContinue')
              )}
            </Button>
          </Grid>
          <Grid item xs={12} md={6}>
            <Button
              fullWidth
              variant="outlined"
              onClick={regenerate}
              disabled={totpSetupLoading || finishing}
            >
              {T.translate('auth.setup.regenerateKey')}
            </Button>
          </Grid>
          {/* <Grid item xs={12}>
            <Button
              fullWidth
              variant="text"
              color="secondary"
              onClick={goBackToSignIn}
              disabled={totpSetupLoading || finishing}
            >
              {T.translate('auth.setup.backToSignIn')}
            </Button>
          </Grid> */}
        </Grid>
      </div>
    </Container>
  );
}
